import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Users, HelpCircle, Lock, Loader2 } from "lucide-react";
import { usePrivacy } from "@/contexts/PrivacyContext";

const PAGE = 25;

const SectionLabel = ({ children, testid }) => (
  <li className="bg-zinc-900/70 px-3.5 py-1.5 text-[11px] font-700 uppercase tracking-wider text-zinc-500" data-testid={testid}>{children}</li>
);

const GuestRow = ({ p, isLast, onOpen, financialPrivacy, showRank, rank }) => {
  const tours = (n) => `${n || 0} ${n === 1 ? "tour" : "tours"}`;
  return (
    <li
      data-testid={`profile-row-${p.name}`}
      onClick={() => onOpen(p.name)}
      className={`group flex cursor-pointer items-center gap-3 border-l-2 px-3.5 py-3.5 transition-colors duration-150 active:bg-white/5 ${
        isLast ? "border-l-[#E0575B] bg-[#9B2226]/10" : "border-l-transparent hover:bg-white/5"
      }`}
    >
      {showRank && (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#9B2226]/15 text-[11px] font-700 text-[#E0575B]" data-testid={`profile-rank-${p.name}`}>{rank}</span>
      )}
      {(p.avatar_thumb || p.avatar || p.party_photo) ? (
        <img src={p.avatar_thumb || p.avatar || p.party_photo} alt="" className="h-9 w-9 shrink-0 rounded-full border border-zinc-700 object-cover" data-testid={`profile-photo-${p.name}`} />
      ) : (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900">
          <Users className="h-4 w-4 text-[#E0575B]" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-500 leading-snug text-zinc-100 line-clamp-2">{p.name}</p>
        <p className="mt-0.5 text-xs text-zinc-400">{tours(p.lifetime_tours)}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2 pl-1">
        {financialPrivacy ? (
          <span className="inline-flex text-zinc-600" title="Financial privacy on"><Lock className="h-3.5 w-3.5" /></span>
        ) : (
          <p className="whitespace-nowrap font-600 text-emerald-300">${(p.lifetime_gratuity || 0).toLocaleString()}</p>
        )}
        <ChevronRight className="h-4 w-4 shrink-0 text-zinc-700 group-hover:text-zinc-400" />
      </div>
    </li>
  );
};

export const GuestsTable = ({ profiles, unknown, topRepeat = [] }) => {
  const navigate = useNavigate();
  const { financialPrivacy } = usePrivacy();
  const [visible, setVisible] = useState(() => Math.max(PAGE, Number(sessionStorage.getItem("guestsVisible")) || PAGE));
  const sentinelRef = useRef(null);

  useEffect(() => { sessionStorage.setItem("guestsVisible", String(visible)); }, [visible]);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) setVisible((v) => Math.min(v + PAGE, profiles.length)); },
      { rootMargin: "500px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [profiles.length, visible]);
  const lastName = typeof window !== "undefined" ? sessionStorage.getItem("lastProfileName") : null;

  const openProfile = (name) => {
    sessionStorage.setItem("lastProfileName", name);
    navigate(`/profile/${encodeURIComponent(name)}`);
  };

  if (!profiles.length && !topRepeat.length) {
    return (
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-12 text-center" data-testid="empty-profiles">
        <p className="font-display text-lg text-zinc-300">No guests yet</p>
        <p className="mt-1 text-sm text-zinc-400">Save a tour and its guest profile will appear here.</p>
      </div>
    );
  }

  const tours = (n) => `${n || 0} ${n === 1 ? "tour" : "tours"}`;

  return (
    <ul className="divide-y divide-zinc-800/70 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/40" data-testid="guests-table">
      {topRepeat.length > 0 && <SectionLabel testid="guests-section-frequent">Most Frequent Guests</SectionLabel>}
      {topRepeat.map((p, i) => (
        <GuestRow key={`top-${p.name}`} p={p} isLast={p.name === lastName} onOpen={openProfile} financialPrivacy={financialPrivacy} showRank rank={i + 1} />
      ))}
      {topRepeat.length > 0 && <SectionLabel testid="guests-section-all">All Guests · A–Z</SectionLabel>}
      {profiles.slice(0, visible).map((p) => (
        <GuestRow key={p.name} p={p} isLast={p.name === lastName} onOpen={openProfile} financialPrivacy={financialPrivacy} />
      ))}
      {visible < profiles.length && (
        <li ref={sentinelRef} className="flex items-center justify-center gap-2 py-4 text-xs text-zinc-400" data-testid="guests-load-more">
          <Loader2 className="h-4 w-4 animate-spin text-[#E0575B]" /> Loading more… ({profiles.length - visible} left)
        </li>
      )}
      {unknown && visible >= profiles.length && (
        <li
          onClick={() => openProfile("Unknown")}
          data-testid="profile-row-unknown"
          className="flex cursor-pointer items-center gap-3 border-t-2 border-zinc-700/60 px-3.5 py-3.5 text-zinc-400 transition-colors duration-150 hover:bg-white/5"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-800 bg-zinc-900">
            <HelpCircle className="h-4 w-4 text-zinc-600" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate italic">Unknown / unnamed tours</p>
            <p className="mt-0.5 text-xs text-zinc-600">{tours(unknown.lifetime_tours)}</p>
          </div>
          <div className="flex shrink-0 items-center gap-2 pl-1">
            {financialPrivacy ? (
              <span className="inline-flex text-zinc-600" title="Financial privacy on"><Lock className="h-3.5 w-3.5" /></span>
            ) : (
              <p className="whitespace-nowrap font-600 text-emerald-500/70">${(unknown.lifetime_gratuity || 0).toLocaleString()}</p>
            )}
            <ChevronRight className="h-4 w-4 shrink-0 text-zinc-700" />
          </div>
        </li>
      )}
    </ul>
  );
};
